import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUpload, FiDownload, FiCheck, FiX, FiAlertCircle,
  FiFile, FiTrash2, FiEye, FiLoader
} from 'react-icons/fi';
import { shedService } from '../../services/shedService';
import { useAuth } from '../../hooks/useAuth';

const CSV_COLUMNS = [
  { key: 'name', label: 'Name', required: true, example: '10x12 Classic Barn' },
  { key: 'brand', label: 'Brand', required: false, example: 'Heritage' },
  { key: 'model', label: 'Model', required: false, example: 'Classic Barn' },
  { key: 'shedType', label: 'Shed Type', required: false, example: 'barn' },
  { key: 'size', label: 'Size', required: false, example: '10x12' },
  { key: 'price', label: 'Price', required: true, example: '4999' },
  { key: 'salePrice', label: 'Sale Price', required: false, example: '4499' },
  { key: 'depositAmount', label: 'Deposit', required: false, example: '500' },
  { key: 'material', label: 'Material', required: false, example: 'Wood' },
  { key: 'bodyColor', label: 'Body Color', required: false, example: 'Barn Red' },
  { key: 'trimColor', label: 'Trim Color', required: false, example: 'White' },
  { key: 'roofColor', label: 'Roof Color', required: false, example: 'Charcoal' },
  { key: 'roofStyle', label: 'Roof Style', required: false, example: 'Gambrel' },
  { key: 'roofMaterial', label: 'Roof Material', required: false, example: 'Architectural Shingles' },
  { key: 'sidingType', label: 'Siding Type', required: false, example: 'LP SmartSide' },
  { key: 'floorType', label: 'Floor Type', required: false, example: 'Treated Plywood' },
  { key: 'foundation', label: 'Foundation', required: false, example: 'Skid' },
  { key: 'doorType', label: 'Door Type', required: false, example: 'double' },
  { key: 'windowType', label: 'Window Type', required: false, example: '22x36_double' },
  { key: 'condition', label: 'Condition', required: false, example: 'New' },
  { key: 'serialNumber', label: 'Serial Number', required: false, example: 'HB-2024-001' },
  { key: 'stockNumber', label: 'Stock Number', required: false, example: 'SH001' },
  { key: 'yearBuilt', label: 'Year Built', required: false, example: '2024' },
  { key: 'warranty', label: 'Warranty', required: false, example: '5-Year Limited' },
  { key: 'warrantyYears', label: 'Warranty Years', required: false, example: '5' },
  { key: 'width', label: 'Width (ft)', required: false, example: '10' },
  { key: 'depth', label: 'Depth (ft)', required: false, example: '12' },
  { key: 'height', label: 'Height (ft)', required: false, example: '9.5' },
  { key: 'squareFeet', label: 'Sq Ft', required: false, example: '120' },
  { key: 'loft', label: 'Loft', required: false, example: 'true' },
  { key: 'workbench', label: 'Workbench', required: false, example: 'false' },
  { key: 'electrical', label: 'Electrical', required: false, example: 'false' },
  { key: 'insulation', label: 'Insulation', required: false, example: 'false' },
  { key: 'hasRamp', label: 'Has Ramp', required: false, example: 'true' },
  { key: 'hasShelving', label: 'Has Shelving', required: false, example: 'false' },
  { key: 'description', label: 'Description', required: false, example: 'Beautiful classic barn shed.' },
];

const BOOL_FIELDS = ['loft', 'workbench', 'electrical', 'insulation', 'hasRamp', 'hasShelving', 'onSale', 'deliveryIncluded', 'setupIncluded'];
const NUM_FIELDS = ['price', 'salePrice', 'depositAmount', 'width', 'depth', 'height', 'squareFeet', 'yearBuilt', 'warrantyYears', 'salePercentage'];

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);
  return { headers, rows };
}

function mapRow(headers, values) {
  const obj = {};
  headers.forEach((h, i) => {
    const col = CSV_COLUMNS.find(c =>
      c.key.toLowerCase() === h.toLowerCase() ||
      c.label.toLowerCase() === h.toLowerCase() ||
      c.key.toLowerCase().replace(/[_\s]/g, '') === h.toLowerCase().replace(/[_\s]/g, '')
    );
    if (col && values[i] !== undefined && values[i] !== '') {
      let val = values[i];
      if (BOOL_FIELDS.includes(col.key)) {
        val = val.toLowerCase() === 'true' || val === '1' || val.toLowerCase() === 'yes';
      } else if (NUM_FIELDS.includes(col.key)) {
        const parsed = parseFloat(val.replace(/[$,]/g, ''));
        val = isNaN(parsed) ? null : parsed;
      }
      obj[col.key] = val;
    }
  });
  return obj;
}

function validateRow(row, idx) {
  const errors = [];
  if (!row.name && !row.brand) errors.push(`Row ${idx + 1}: Name or Brand is required`);
  if (!row.price && row.price !== 0) errors.push(`Row ${idx + 1}: Price is required`);
  if (row.price && row.price < 0) errors.push(`Row ${idx + 1}: Price cannot be negative`);
  return errors;
}

const CSVBulkUpload = ({ onComplete }) => {
  const { dealerId } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const { headers: h, rows } = parseCSV(text);
      setHeaders(h);
      const mapped = rows.map(r => mapRow(h, r)).filter(r => Object.keys(r).length > 0);
      setParsedRows(mapped);

      const allErrors = mapped.flatMap((r, i) => validateRow(r, i));
      setErrors(allErrors);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (errors.length > 0) return;
    setImporting(true);
    setStep('importing');

    const validRows = parsedRows.map(row => ({
      ...row,
      status: row.status || 'available',
      condition: row.condition || 'New',
      depositAmount: row.depositAmount || 500,
      deliveryIncluded: row.deliveryIncluded ?? true,
      setupIncluded: row.setupIncluded ?? true,
    }));

    const result = await shedService.bulkCreateSheds(validRows, dealerId, null);
    setImportResult(result);
    setImporting(false);
    setStep('done');
  };

  const handleDownloadTemplate = () => {
    const headerLine = CSV_COLUMNS.map(c => c.label).join(',');
    const exampleLine = CSV_COLUMNS.map(c => c.example).join(',');
    const csv = headerLine + '\n' + exampleLine + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shed-inventory-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep('upload');
    setFileName('');
    setParsedRows([]);
    setHeaders([]);
    setErrors([]);
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Bulk CSV Upload</h2>
          <p className="text-sm text-gray-500 mt-1">Import multiple sheds at once from a CSV file</p>
        </div>
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          <FiDownload size={16} />
          Download Template
        </button>
      </div>

      {/* Upload Step */}
      {step === 'upload' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-emerald-400 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFile}
            className="hidden"
          />
          <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">Drop your CSV file here or click to browse</p>
          <p className="text-gray-400 text-sm mt-2">Supports .csv files with headers matching the template</p>
        </motion.div>
      )}

      {/* Preview Step */}
      {step === 'preview' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {/* File info */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <FiFile className="text-emerald-600" />
              <span className="text-sm font-medium text-gray-700">{fileName}</span>
              <span className="text-xs text-gray-400">{parsedRows.length} rows found</span>
            </div>
            <button onClick={reset} className="text-gray-400 hover:text-red-500 transition-colors">
              <FiTrash2 size={16} />
            </button>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-2">
                <FiAlertCircle />
                {errors.length} validation error{errors.length > 1 ? 's' : ''} found
              </div>
              <ul className="text-red-600 text-xs space-y-1 max-h-32 overflow-y-auto">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {/* Preview table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">#</th>
                    {['Name', 'Type', 'Size', 'Price', 'Material', 'Condition', 'Serial #'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {parsedRows.slice(0, 50).map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{row.name || row.brand || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{row.shedType || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{row.size || '—'}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium">{row.price ? `$${Number(row.price).toLocaleString()}` : '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{row.material || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{row.condition || 'New'}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono">{row.serialNumber || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedRows.length > 50 && (
              <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 text-center">
                Showing first 50 of {parsedRows.length} rows
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={reset}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={errors.length > 0 || parsedRows.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <FiUpload size={16} />
              Import {parsedRows.length} Shed{parsedRows.length !== 1 ? 's' : ''}
            </button>
          </div>
        </motion.div>
      )}

      {/* Importing Step */}
      {step === 'importing' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">Importing {parsedRows.length} sheds...</p>
          <p className="text-gray-400 text-sm mt-1">This may take a moment</p>
        </motion.div>
      )}

      {/* Done Step */}
      {step === 'done' && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
          {importResult?.error ? (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiX className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Import Failed</h3>
              <p className="text-red-600 text-sm">{importResult.error.message || JSON.stringify(importResult.error)}</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Import Complete</h3>
              <p className="text-gray-600 text-sm">
                Successfully imported <strong>{importResult?.inserted || 0}</strong> sheds.
                They are unpublished by default — go to Shed Inventory to review and publish them.
              </p>
            </>
          )}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={reset}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm transition-colors"
            >
              Import More
            </button>
            {onComplete && (
              <button
                onClick={onComplete}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Go to Shed Inventory
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Help section */}
      {step === 'upload' && (
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="font-medium text-gray-900 text-sm mb-3">How it works</h3>
          <ol className="space-y-2 text-sm text-gray-600">
            <li><strong>1.</strong> Download the CSV template above</li>
            <li><strong>2.</strong> Fill in your shed data (Name and Price are required)</li>
            <li><strong>3.</strong> Upload the file and review the preview</li>
            <li><strong>4.</strong> Click Import — sheds are created as unpublished drafts</li>
            <li><strong>5.</strong> Review each shed in Shed Inventory, add photos and publish</li>
          </ol>
          <div className="mt-4 text-xs text-gray-400">
            <strong>Tip:</strong> Column headers are flexible. "Shed Type", "shed_type" and "shedType" all work.
            Boolean fields accept "true", "false", "yes", "no" or "1", "0".
          </div>
        </div>
      )}
    </div>
  );
};

export default CSVBulkUpload;
